import { useFeatureFlag } from "#webapp/src/launch-darkly/useLaunchDarkly";
import { isImpersonated } from "@clockwise/client-commons/src/util/jwt";
import { jwt } from "@clockwise/web-commons/src/util/local-storage";
import { animated, config, useTransition } from "@react-spring/web";
import classNames from "classnames";
import { isEmpty } from "lodash";
import React, { useMemo, useRef } from "react";
import { FeedEmptyMessage } from "./FeedEmptyMessage";
import { SidebarQuickFeed } from "./Sidebar/SidebarQuickFeed";
import { AILoadingMessages } from "./components/AILoadingMessages";
import { AssistantMessage } from "./components/AssistantMessage";
import { UserMessage } from "./components/UserMessage";
import { useAIMessageContext } from "./hooks/AIMessageContext";
import { GQLAssistantMessageDetails } from "./utils/types";

export const AIChatFeed = () => {
  const [isNewPrismUI] = useFeatureFlag("rollout-prism");
  const { messages, debugInfoMap, processingMessage, shouldExcludeAi } = useAIMessageContext();
  const feedRef = useRef(null);

  const renderedMessages = useMemo(() => {
    // filter out optimistic response AssistantMessage
    const filteredMessages = messages.filter(
      (message: GQLAssistantMessageDetails) =>
        !(message.__typename === "TextResponse" && isEmpty(message.text)),
    );

    return filteredMessages.map((message) => {
      const getDebugInfoForMessage = (messageId: string) => {
        const debugInfoForId = debugInfoMap[messageId];
        return debugInfoForId ?? undefined;
      };
      const debugInfo = getDebugInfoForMessage(message.id);
      const isLastMessage = messages[messages.length - 1].id === message.id;

      // Bail out if we have an empty UserMessage
      if (message.__typename === "UserMessage" && message.text === "") {
        return <div key={message.id} />;
      }

      return (
        <div
          key={message.id}
          className={classNames("cw-my-2 cw-max-w-full", {
            "cw-self-stretch": message.__typename !== "UserMessage",
            "cw-self-end": message.__typename === "UserMessage",
          })}
        >
          {message.__typename === "UserMessage" && <UserMessage message={message} />}
          {message.__typename !== "UserMessage" && (
            <AssistantMessage
              debugInfo={debugInfo}
              isLastMessage={isLastMessage}
              message={message}
            />
          )}
        </div>
      );
    });
  }, [messages, debugInfoMap]);

  const animatedMessages = useTransition(renderedMessages, {
    from: { translateY: 5 },
    enter: { translateY: 0 },
    config: config.stiff,
    trail: feedRef.current ? 50 : 0,
    immediate: !feedRef.current,
    key: (message: JSX.Element) => message.key ?? JSON.stringify(message.props).substring(0, 10),
  });

  const showEmptyState = messages.length === 0 && !processingMessage;

  const isImpersonatedUser = isImpersonated(jwt.get());

  return (
    <>
      {showEmptyState &&
        (isNewPrismUI ? (
          <SidebarQuickFeed />
        ) : (
          <div className="cw-py-2 cw-px-6">
            <FeedEmptyMessage
              isImpersonatedUser={isImpersonatedUser}
              shouldExcludeAi={shouldExcludeAi}
            />
          </div>
        ))}
      {!showEmptyState && (
        <>
          {animatedMessages((style, message) => (
            <animated.div style={style} className="cw-mb-4 cw-flex cw-flex-col" ref={feedRef}>
              {message}
            </animated.div>
          ))}
          {processingMessage && <AILoadingMessages />}
        </>
      )}
    </>
  );
};
